import type { Column } from '@shared/types/data-table'
import type { MmPayout } from '@features/money-movement/types/mm-payout.types'
import type { ColumnManagementConfig } from '@shared/lib/tables/utils/column-management'
import { createStatusColumn, createDateColumn, createTextColumn, createCurrencyColumn } from './column-factories'

const methodLabels: Record<string, string> = {
  ach: 'ACH',
  wire: 'Wire',
  rtp: 'RTP',
  check: 'Check',
  internal: 'Internal',
}

export const MM_PAYOUT_COLUMNS: Record<string, Column<MmPayout>> = {
  merchantName: createTextColumn<MmPayout>('merchantName', 'Merchant', 200),
  status: createStatusColumn<MmPayout>('mm-payout'),
  method: createTextColumn<MmPayout>(
    'method', 'Method', 100, undefined, undefined,
    (value: string) => methodLabels[value] || value
  ),
  currency: createTextColumn<MmPayout>('currency', 'Currency', 90),
  amount: createCurrencyColumn<MmPayout>('amount', 'Amount', 140),
  destinationAccount: createTextColumn<MmPayout>('destinationAccount', 'Destination', 140),
  reference: createTextColumn<MmPayout>('reference', 'Reference', 180),
  scheduledDate: createTextColumn<MmPayout>('scheduledDate', 'Scheduled', 120),
  completedDate: createTextColumn<MmPayout>('completedDate', 'Completed', 120, undefined, 'Pending'),
  createdAt: createDateColumn<MmPayout>('createdAt', 'Created', 150),
}

export const MM_PAYOUT_STATUS_CONFIG = {
  all: ['merchantName', 'status', 'method', 'currency', 'amount', 'destinationAccount', 'reference', 'scheduledDate', 'createdAt'],
  completed: ['merchantName', 'status', 'method', 'currency', 'amount', 'destinationAccount', 'completedDate'],
  pending: ['merchantName', 'status', 'method', 'currency', 'amount', 'scheduledDate', 'createdAt'],
  'in-transit': ['merchantName', 'status', 'method', 'currency', 'amount', 'scheduledDate', 'reference'],
  failed: ['merchantName', 'status', 'method', 'currency', 'amount', 'reference', 'createdAt'],
  cancelled: ['merchantName', 'status', 'method', 'currency', 'amount', 'reference', 'createdAt'],
} as const

export const getMmPayoutColumns = (status: string = 'all'): Column<MmPayout>[] => {
  const keys = MM_PAYOUT_STATUS_CONFIG[status as keyof typeof MM_PAYOUT_STATUS_CONFIG] || MM_PAYOUT_STATUS_CONFIG.all
  return keys.map(key => MM_PAYOUT_COLUMNS[key]).filter(Boolean)
}

export const getMmPayoutColumnConfig = (status: string = 'all'): ColumnManagementConfig[] => {
  const visibleKeys: Set<string> = new Set(MM_PAYOUT_STATUS_CONFIG[status as keyof typeof MM_PAYOUT_STATUS_CONFIG] || MM_PAYOUT_STATUS_CONFIG.all)
  return Object.entries(MM_PAYOUT_COLUMNS).map(([key, col], index) => ({
    id: col.key,
    label: typeof col.header === 'string' ? col.header : col.key,
    visible: visibleKeys.has(key),
    required: ['merchantName', 'status'].includes(key),
    order: index
  }))
}
